import React, { useRef, useEffect } from 'react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useLanguage } from '@/contexts/LanguageContext';
import { format } from 'date-fns';
import { CalendarEvent } from '../types';

interface DeleteShiftConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  shift?: CalendarEvent;
  isDeleting?: boolean;
}

const DeleteShiftConfirmationDialog: React.FC<DeleteShiftConfirmationDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  shift,
  isDeleting = false
}) => {
  const { t } = useLanguage();
  const deleteButtonRef = useRef<HTMLButtonElement>(null);

  // Auto-focus the delete button when dialog opens
  useEffect(() => {
    if (isOpen && deleteButtonRef.current) {
      setTimeout(() => {
        deleteButtonRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  // Handle keyboard events
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) return;
      
      if (event.key === 'Enter' && !isDeleting) {
        event.preventDefault();
        onConfirm();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, isDeleting, onConfirm, onClose]);

  if (!shift) return null;

  const formatShiftTime = (startDate: string, endDate: string) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    return `${format(start, 'MMM d, yyyy HH:mm')} - ${format(end, 'HH:mm')}`;
  };

  const getGuardNames = () => {
    if (shift.guard_names && shift.guard_names.length > 0) {
      return shift.guard_names.join(', ');
    }
    if (shift.assigned_guards && shift.assigned_guards.length > 0) {
      return `${shift.assigned_guards.length} ${t('assignedGuards') || 'assigned guards'}`;
    }
    return t('noGuardsAssigned') || 'No guards assigned';
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={onClose}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('deleteShift') || 'Delete Shift'}</AlertDialogTitle>
          <AlertDialogDescription className="space-y-2">
            <p>{t('deleteShiftConfirmation') || 'Are you sure you want to delete this shift? This action cannot be undone.'}</p>
            
            <div className="mt-4 p-3 bg-muted rounded-md space-y-1">
              <div className="font-medium">{shift.title}</div>
              <div className="text-sm text-muted-foreground">
                {formatShiftTime(shift.start_date, shift.end_date)}
              </div>
              {shift.location && (
                <div className="text-sm text-muted-foreground">
                  {t('location') || 'Location'}: {shift.location}
                </div>
              )}
              <div className="text-sm text-muted-foreground">
                {t('guards') || 'Guards'}: {getGuardNames()}
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isDeleting}>
            {t('cancel') || 'Cancel'}
          </AlertDialogCancel>
          <AlertDialogAction 
            ref={deleteButtonRef}
            onClick={onConfirm}
            disabled={isDeleting}
            autoFocus
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isDeleting ? (
              <>
                <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                {t('deleting') || 'Deleting...'}
              </>
            ) : (
              t('delete') || 'Delete'
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default DeleteShiftConfirmationDialog;